import { InitPage } from "../index";
import swal from "sweetalert";
import * as Toastr from "toastr";

declare var clientId: number;
declare var clientCode: string;
declare var epcSettingProfileId: number;
declare var epcProfileLocationRestrictionsPath: string;

// Fallback to window object if declared vars are not available
const getClientId = (): number => {
  return (window as any).clientId || clientId || 0;
};

const getEpcSettingProfileId = (): number => {
  return (window as any).epcSettingProfileId || epcSettingProfileId || 0;
};

const getApiBasePath = (): string => {
  // Use the Rails path helper if available, otherwise construct from client code
  if ((window as any).epcProfileLocationRestrictionsPath) {
    return (window as any).epcProfileLocationRestrictionsPath.replace(/\/$/, '');
  }
  const code = (window as any).clientCode || clientCode || '';
  return `/${code}/epc_profile_location_restrictions`;
};

class EpcProfileLocationRestriction {
  private table: any;
  private stores: any[] = [];
  private restrictions: any[] = [];
  private apiBasePath: string;

  constructor() {
    this.apiBasePath = getApiBasePath();
    this.init();
  }

  init() {
    $('#location-restriction-btn').on('click', () => {
      this.loadData();
      $('#location-restrictions-modal').modal('show');
    });

    $('#location-restrictions-modal').on('shown.bs.modal', () => {
      this.initializeTable();
      this.loadStores();
    });

    $('#location-restrictions-modal').on('hidden.bs.modal', () => {
      // Destroy Select2 when modal is hidden to prevent memory leaks
      const select = $('#location-restriction-select');
      if (select.hasClass('select2-hidden-accessible')) {
        select.select2('destroy');
      }
    });

    $('#add-location-restriction-btn').on('click', () => {
      this.addRestriction();
    });

    $(document).on('click', '.remove-location-restriction', (e) => {
      const restrictionId = $(e.currentTarget).data('restriction-id');
      this.removeRestriction(restrictionId);
    });
  }

  private loadData() {
    $.ajax({
      url: this.apiBasePath,
      method: 'GET',
      data: { epc_setting_profile_id: getEpcSettingProfileId() },
      dataType: 'json',
      success: (response: any) => {
        if (response.success) {
          this.restrictions = response.restrictions || [];
          this.stores = response.stores || [];
          // Don't populate dropdown here - wait for modal to be shown
          // populateStoresDropdown() will be called when modal is shown
          if (this.table) {
            this.refreshTable();
          }
        }
      },
      error: () => {
        Toastr.error('Error loading location restrictions', 'Error', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                progressBar: true,
                positionClass: 'toast-top-right'
              });
      }
    });
  }

  private loadStores() {
    if (this.stores.length === 0) {
      $.ajax({
        url: this.apiBasePath,
        method: 'GET',
        data: { epc_setting_profile_id: getEpcSettingProfileId() },
        dataType: 'json',
        success: (response: any) => {
          if (response.success) {
            this.stores = response.stores || [];
            this.populateStoresDropdown();
          }
        }
      });
    } else {
      this.populateStoresDropdown();
    }
  }

  private populateStoresDropdown() {
    const select = $('#location-restriction-select');
    
    // Destroy Select2 if it exists
    if (select.hasClass('select2-hidden-accessible')) {
      select.select2('destroy');
    }
    
    // Filter out already added locations and sort by code
    const addedLocationIds = this.restrictions.map(r => r.location_id);
    const availableStores = this.stores
      .filter((store: any) => !addedLocationIds.includes(store.id))
      .sort((a: any, b: any) => {
        const codeA = (a.code || '').toLowerCase();
        const codeB = (b.code || '').toLowerCase();
        if (codeA !== codeB) {
          return codeA.localeCompare(codeB);
        }
        return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      });

    // Clear and rebuild options
    select.empty();
    select.append('<option value="">Select a location...</option>');
    
    availableStores.forEach((store: any) => {
      const displayText = store.code ? `${store.code} - ${store.name}` : store.name;
      select.append(`<option value="${store.id}">${displayText}</option>`);
    });

    // Initialize Select2
    select.select2({
      placeholder: 'Select a location...',
      allowClear: true,
      width: '100%',
      dropdownParent: $('#location-restrictions-modal'),
      minimumResultsForSearch: 0
    });
  }

  private initializeTable() {
    if (this.table) {
      this.table.destroy();
    }

    this.table = ($('#locationRestrictionsTable') as any).DataTable({
      bDestroy: true,
      paging: false,
      searching: false,
      info: false,
      ordering: false,
      data: this.restrictions,
      columns: [
        { data: 'location', defaultContent: '', className: 'text-left' },
        {
          data: null,
          defaultContent: '',
          className: 'sn-icons text-center',
          render: (data: any, type: string, row: any) => {
            return `<button type="button" class="sn-transparent-btn remove-location-restriction" data-restriction-id="${row.id}">
                      <i class="fa fa-minus-circle fa-2x circle-delete-icon" title="Remove" data-toggle="tooltip"></i>
                    </button>`;
          }
        }
      ]
    });

    this.refreshTable();
  }

  private refreshTable() {
    if (this.table) {
      this.table.clear();
      this.table.rows.add(this.restrictions);
      this.table.draw();
      $('[data-toggle="tooltip"]').tooltip();
    }
  }

  private addRestriction() {
    const locationId = $('#location-restriction-select').val();
    const profileId = getEpcSettingProfileId();
    
    if (!profileId || profileId === 0) {
      swal({
        title: 'Warning',
        text: 'Please save the EPC Setting Profile first before adding location restrictions',
        icon: 'warning',
        closeOnClickOutside: false,
        buttons: {
          cancel: {
            text: 'Close',
            value: null,
            visible: true,
            className: 'sn-btn sn-btn-sm sn-btn-silver',
            closeModal: true
          }
        }
      });
      return;
    }
    
    if (!locationId) {
      Toastr.error('No location selected', 'Error', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                progressBar: true,
                positionClass: 'toast-top-right'
              });
      return;
    }

    $.ajax({
      url: this.apiBasePath,
      method: 'POST',
      data: {
        epc_setting_profile_id: getEpcSettingProfileId(),
        public_tenant_location_id: locationId
      },
      dataType: 'json',
      success: (response: any) => {
        if (response.success) {
          this.restrictions.push(response.restriction);
          this.populateStoresDropdown();
          this.refreshTable();
          
          // Clear Select2 value
          const select = $('#location-restriction-select');
          select.val(null).trigger('change');

          // Show success toast
          const locationName = response.restriction.location_name || 'Location';
          Toastr.success('Location restriction added successfully.', 'Success', {
            timeOut: 3000,
            extendedTimeOut: 1000,
            progressBar: true,
            positionClass: 'toast-top-right'
          });
        } else {
          const errorMsg = response.errors ? (Array.isArray(response.errors) ? response.errors.join(', ') : response.errors) : 'Unknown error';
          Toastr.error(`Error adding location restriction: ${errorMsg}`, 'Error', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                progressBar: true,
                positionClass: 'toast-top-right'
              });
        }
      },
      error: (xhr: any) => {
        const error = xhr.responseJSON?.errors || ['Error adding location restriction'];
        const errorMsg = Array.isArray(error) ? error.join(', ') : error;
        Toastr.error(`Error: ${errorMsg}`, 'Error', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                progressBar: true,
                positionClass: 'toast-top-right'
              });
      }
    });
  }

  private removeRestriction(restrictionId: number) {
    const restriction = this.restrictions.find(r => r.id === restrictionId);
    const locationName = restriction?.location_name || 'this location';
    
    swal({
      title: 'Are you sure?',
      text: 'Are you sure you want to remove this location restriction?',
      icon: 'warning',
      closeOnClickOutside: false,
      className: 'sn-m-modal',
      buttons: {
        cancel: {
          text: 'No',
          value: null,
          visible: true,
          className: 'sn-btn sn-btn-sm sn-btn-silver',
          closeModal: true
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: 'sn-btn sn-btn-sm sn-btn-red',
          closeModal: true
        }
      },
      dangerMode: true
    }).then((willDelete) => {
      if (willDelete) {
        $.ajax({
          url: `${this.apiBasePath}/${restrictionId}`,
          method: 'DELETE',
          dataType: 'json',
          success: (response: any) => {
            if (response.success) {
              this.restrictions = this.restrictions.filter(r => r.id !== restrictionId);
              this.populateStoresDropdown();
              this.refreshTable();

              // Show success toast
              Toastr.success('Location restriction removed successfully.', 'Success', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                progressBar: true,
                positionClass: 'toast-top-right'
              });
            } else {
              Toastr.error('Error removing location restriction', 'Error', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                progressBar: true,
                positionClass: 'toast-top-right'
              });
            }
          },
          error: () => {
            Toastr.error('Error removing location restriction', 'Error', {
                timeOut: 3000,
                extendedTimeOut: 1000,
                progressBar: true,
                positionClass: 'toast-top-right'
              });
          }
        });
      }
    });
  }
}

export function LoadPage() {
  InitPage("epc_setting_profiles", () => {
    new EpcProfileLocationRestriction();
  });
}
